import merge from 'lodash.merge';
import icon from '../util/icon';

let modalSequence = 0;
// how many modals are currently open — lets stacked modals (e.g. the ARC
// panel opening a reason/link dialog) share the body scroll lock without one
// closing dropping it for the others
let openModalCount = 0;

/**
 * Hold the page still while the body scroll lock is on.
 *
 * `body.has-modal { overflow: hidden }` takes the window scrollbar away with it,
 * and on Windows and Linux — where the scrollbar occupies real layout width,
 * unlike the overlay scrollbars on macOS — the page content widens by about 15px
 * at the moment a modal opens and snaps back when it closes. Behind a
 * translucent scrim you see it happen. Replacing the missing scrollbar with
 * padding of the same width holds the in-flow layout still.
 *
 * Only in-flow content: an element positioned against the viewport is measured
 * from the viewport, which is what actually changed, so body padding cannot
 * reach it. That is the app sidebar, which is a flat dark panel with nothing
 * anchored to its right edge — nothing there reads as movement.
 *
 * Applied on the first open and released on the last close, so stacked modals
 * do not double the padding or drop it early — same accounting as the lock
 * itself.
 */
const setScrollLock = locked => {

  if(locked) {

    const gutter = window.innerWidth - document.documentElement.clientWidth;

    if(gutter > 0) {
      document.body.style.paddingRight = `${gutter}px`;
    }

  } else {
    document.body.style.paddingRight = '';
  }

  document.body.classList.toggle('has-modal', locked);

};

class Modal {

  constructor (settings = {}) {

    settings = merge({
      title: '',
      content: '',
      classes: [],
      maxWidth: 800,
      showNav: false,
      navIndex: 0,
      navList: [],
      onClose: null,
    }, settings);

    this.settings = settings;
    this.modal = document.createElement('div');
    this.modal.classList.add('modal');

    this.titleId = `modal-title-${++modalSequence}`;
    this.modal.setAttribute('role', 'dialog');
    this.modal.setAttribute('aria-modal', 'true');
    this.modal.setAttribute('aria-labelledby', this.titleId);

    this.currentIndex = this.settings.navIndex;
    this.navList = this.settings.navList;

    // keep one bound reference so the keyup listener can actually be removed
    // (binding inline creates a new function each time and leaks the listener)
    this.boundKeyUp = this.onKeyUpEvent.bind(this);
    this.boundKeyDown = this.onKeyDownEvent.bind(this);

    settings.classes.map(c => {
      this.modal.classList.add(c);
    });

    this.modal.innerHTML = `
    <div class="modal-content" tabindex="-1">
      <div class="modal-header">
        <h2 class="modal-header__title" id="${this.titleId}">${settings.title}</h2>
        <button type="button" class="modal__close" aria-label="Close">${icon('times')}</button>
      </div>
      <div class="modal__content"></div>
      ${this.settings.showNav ? `
        <button type="button" class="modal-nav-previous ${this.currentIndex > 0 ? 'is-active' : ''}" aria-label="Previous">${icon('chevron-left')}</button>
        <button type="button" class="modal-nav-next ${this.currentIndex + 1 < this.navList.length ? 'is-active' : ''}" aria-label="Next">${icon('chevron-right')}</button>
      `: ''}
    </div>
    `;

    this.modalContainer = this.modal.querySelector('.modal-content');

    this.title = this.modal.querySelector('.modal-header__title');
    this.content = this.modal.querySelector('.modal__content');

    this.closeButton = this.modal.querySelector('.modal__close');

    if(this.settings.content.length > 0) {
      this.setContent(this.settings.content);
    }

    if(this.settings.showNav && this.settings.navList.length > 0) {

      this.prevButton = this.modal.querySelector('.modal-nav-previous');
      this.nextButton = this.modal.querySelector('.modal-nav-next');

      this.prevButton.addEventListener('click', this.previousItem.bind(this));
      this.nextButton.addEventListener('click', this.nextItem.bind(this));

    }

    this.closeButton.addEventListener('click', () => {
      this.close();
    });

    this.modal.addEventListener('mousedown', e => {
      if(e.target === this.modal) {
        this.close();
      }
    });

    document.addEventListener('keyup', this.boundKeyUp);
    this.modal.addEventListener('keydown', this.boundKeyDown);
    document.body.appendChild(this.modal);

  }

  updateContentToNavItem() {

    if(this.navList.length > 0 && this.navList[this.currentIndex]) {

      const item = this.navList[this.currentIndex];
      this.setTitle(item.title);
      this.setContent(item.content);

    }

  }

  previousItem() {
    if(this.currentIndex > 0) {

      this.currentIndex--;
      this.nextButton.classList.toggle('is-active', true);

      if(this.currentIndex === 0) {
        this.prevButton.classList.remove('is-active');
      } else {
        this.prevButton.classList.toggle('is-active', true);
      }

      this.updateContentToNavItem();

    }
  }

  nextItem() {

    if(this.currentIndex + 1 < this.navList.length) {

      this.currentIndex++;
      this.prevButton.classList.toggle('is-active', true);

      if(this.currentIndex === this.navList.length - 1) {
        this.nextButton.classList.remove('is-active');
      } else {
        this.nextButton.classList.toggle('is-active', true);
      }

      this.updateContentToNavItem();

    }
  }

  onKeyUpEvent(e) {

    // only the top-most modal responds to Escape when several are stacked
    const modals = document.querySelectorAll('.modal');

    if(e.key === 'Escape' && modals.length && modals[modals.length - 1] === this.modal) {
      this.close();
    }
  }

  // Keep Tab focus inside the open modal.
  onKeyDownEvent(e) {

    if(e.key !== 'Tab') {
      return;
    }

    const focusable = this.modal.querySelectorAll(
      'a[href], button:not([disabled]), textarea:not([disabled]), input:not([disabled]), select:not([disabled]), [tabindex]:not([tabindex="-1"])'
    );

    if(!focusable.length) {
      return;
    }

    const first = focusable[0];
    const last = focusable[focusable.length - 1];
    const active = document.activeElement;

    if(e.shiftKey && (active === first || active === this.modalContainer)) {
      e.preventDefault();
      last.focus();
    } else if(!e.shiftKey && active === last) {
      e.preventDefault();
      first.focus();
    }
  }

  getContainer() {
    return this.modal;
  }

  setTitle(html) {
    this.title.innerHTML = html;
  }

  getContentContainer() {
    return this.modalContainer;
  }

  setContent(html) {

    this.content.innerHTML = html;

    if(window.lazyImages) {
      window.lazyImages.maybeProcessItems(this.content);
    }

  }

  // Centering is handled entirely in CSS (fixed-position flexbox); retained as
  // a no-op so existing callers keep working.
  offsetContainer() {}

  open() {

    // remember page position + what had focus so we can restore both on close
    this.startPagePosition = $(window).scrollTop();
    this.previouslyFocused = document.activeElement;

    // Measured before the class goes on, while the scrollbar is still there.
    if(openModalCount === 0) {
      setScrollLock(true);
    }

    this.modal.classList.add('is-open');
    this.isOpen = true;
    openModalCount++;

    if(this.settings.maxWidth) {
      $(this.modalContainer).css('maxWidth', this.settings.maxWidth + 'px');
    }

    // move focus into the dialog (its container, not a deep field) so keyboard
    // and screen-reader users start at the top and Escape / Tab-trap work
    this.modalContainer.focus({ preventScroll: true });

  }

  close() {

    if(this.settings.onClose) {
      this.settings.onClose();
    }

    document.removeEventListener('keyup', this.boundKeyUp);

    if(Array.from(document.body.childNodes).includes(this.modal)) {
      document.body.removeChild(this.modal);

      if(this.isOpen) {
        openModalCount = Math.max(0, openModalCount - 1);
        this.isOpen = false;
      }

      // only release the body scroll lock once the last modal has closed
      if(openModalCount === 0) {
        setScrollLock(false);
      }
    }

    // put us back
    $(window).scrollTop(this.startPagePosition);

    // restore focus to whatever opened the modal
    if(this.previouslyFocused && typeof this.previouslyFocused.focus === 'function') {
      this.previouslyFocused.focus({ preventScroll: true });
    }

  }

}

export default Modal;
